import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield, Loader2 } from "lucide-react";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) { setError("Missing invite token"); return; }
    api.get(`/invites/${token}`).then(({ data }) => setInvite(data))
      .catch((err) => setError(formatApiError(err.response?.data?.detail)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post(`/invites/${token}/accept`, { password });
      setUser(data);
      toast.success("Welcome to the team!");
      navigate("/app", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0B0F19] px-6 text-white grid-bg">
      <div className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900/70 p-8">
        <div className="flex items-center gap-2 font-heading text-xl font-bold"><Shield className="h-6 w-6 text-primary" /> IShareLeads</div>
        {error ? (
          <div className="mt-6" data-testid="invite-error">
            <h1 className="font-heading text-2xl font-bold">Invite unavailable</h1>
            <p className="mt-2 text-sm text-slate-400">{error}</p>
            <Link to="/login"><Button className="mt-6 w-full">Go to login</Button></Link>
          </div>
        ) : !invite ? (
          <div className="mt-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <form onSubmit={submit} className="mt-6">
            <h1 className="font-heading text-2xl font-bold">Join {invite.org_name}</h1>
            <p className="mt-2 text-sm text-slate-400">You've been invited as <span className="capitalize text-white">{invite.role}</span>. Set a password to activate <span className="text-white">{invite.email}</span>.</p>
            <div className="mt-6">
              <Label htmlFor="pw">Create password</Label>
              <Input id="pw" type="password" value={password} required minLength={6} data-testid="invite-password-input"
                onChange={(e) => setPassword(e.target.value)} placeholder="Min. 6 characters" className="mt-1.5 bg-slate-950" />
            </div>
            <Button type="submit" disabled={loading} data-testid="invite-accept-button" className="mt-6 w-full">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Accept &amp; join
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
